import { useContext, useEffect, useMemo, useState } from 'react';

import { MeetingRoom, Edit } from '@mui/icons-material';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

import { type SelectableItem } from './types';
import { getGroupLabelsForSession } from './util';
import { ClassContext } from '../../context/ClassContext';
import { type ClassSessionInfo, Roles } from '../../context/types';
import { getUserUuidHeaders } from '../../utils/httpUtils';
import { getUrlParams, processApiPath } from '../../utils/url';
import Loadable from '../common/loadable';

export const SelectDeepLinkContent = () => {
    const {
        me,
        meFetched,
        adminSettings,
        classData,
        fetchClassInfo,
        userUuid,
        classUuid,
        previousSessions,
        upcomingSessions,
        sessions,
        isLoadingClassData
    } = useContext(ClassContext);
    const [selectableItems, setSelectableItems] = useState<SelectableItem[]>([]);
    const [selectedItem, setSelectedItem] = useState<SelectableItem | undefined>(undefined);
    const [selectedSessionUuid, setSelectedSessionUuid] = useState<string | null>(null);
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const { role, user_name: username } = me;
    const {
        lti_uuid: ltiUuid,
        is_guest: isGuestParam,
        from_scheduler: fromScheduler,
        from_teams: fromTeams
    } = getUrlParams();
    const isInstructor = useMemo(() => role === Roles.Teacher, [role]);
    const isAssistant = useMemo(() => role === Roles.Assistant, [role]);
    const isPrivilegedUser = useMemo(() => isInstructor || isAssistant, [isAssistant, isInstructor]);
    const { class: classInfo, classGroups, userGroups, lead_instructor: leadInstructor } = classData;
    const { lms_type: lmsType } = classInfo;
    const {
        sid: classId,
        class_name: className,
        meeting_id: meetingId,
        password: passcode,
        meeting_provider: meetingProvider,
        lms_sync_settings: lmsSyncSettings,
        lms_type: apiType
    } = classInfo;
    const isZoomProvider = meetingProvider && meetingProvider.trim().toLowerCase() === 'zoom';
    const { t } = useTranslation(['lti', 'common', 'class']);
    useEffect(() => {
        if (!sessions) {
            return;
        }
        const createSessionLabel = (session: ClassSessionInfo) => {
            const start = `${session.date_str} ${session.start_time_str}`;
            const end = session.end_time_str;

            const topic = session.class_session_topic || t('class-session');
            return `${topic} ${start} - ${end}`;
        };
        const createSubtitle = (session: ClassSessionInfo) => {
            const groupLabels = getGroupLabelsForSession(session, classGroups, userGroups, true, me.role);
            if (groupLabels.length === 1) {
                if (groupLabels[0].section === 'all') {
                    const label = `${t('class:all-sections')} ${groupLabels[0].label}`;
                    return `${label} ${session.date_str} ${session.start_time_str} - ${session.end_time_str}`;
                } else {
                    const label = `${groupLabels[0].section} ${groupLabels[0].label}`;
                    return `${label} ${session.date_str} ${session.start_time_str} - ${session.end_time_str}`;
                }
            }
            return `${session.date_str} ${session.start_time_str} - ${session.end_time_str}`;
        };
        const items: SelectableItem[] = [];
        sessions.forEach((session) => {
            items.push({
                topic: session.class_session_topic,
                title: session.class_session_topic || t('class-session'),
                subTitle: createSubtitle(session),
                text: createSessionLabel(session),
                start: new Date(Number(session.start_time) * 1000).toISOString(),
                end: new Date(Number(session.end_time) * 1000).toISOString(),
                type: '',
                classUuid: session.class_uuid,
                sessionUuid: session.class_session_uuid
            });
        });

        setSelectableItems(items.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()));
    }, [t, sessions, classGroups, userGroups, me.role]);

    const submitDeepLinkPost = () => {
        async function getDLReqeust(selectedItems) {
            if (!ltiUuid && !lmsType) {
                console.log('LTI UUID and LMS Type are required');
                return;
            }

            const response = await axios.post(
                processApiPath(`/v1/lti/${lmsType}/create-dl-response-message`),
                { lti_uuid: ltiUuid, selectedItems },
                { headers: getUserUuidHeaders(userUuid) }
            );
            const { JWT, action } = response.data;
            const form = document.getElementById('lti-deep-link-form') as HTMLFormElement;
            const input = form.querySelector('input[name="JWT"]') as HTMLInputElement;
            input.value = JWT;
            form.action = action;
            form.submit();
        }
        if (!selectedItem) {
            console.log('No item selected');
            return;
        }
        const selectedItems = [selectedItem];
        getDLReqeust(selectedItems);
    };

    const handleListItemClick = (sessionUuid: string) => (event: React.MouseEvent<HTMLDivElement>) => {
        event.preventDefault();
        const item = selectableItems.find((i) => i.sessionUuid === sessionUuid);
        setSelectedItem(item);
    };

    const editSessionTitle = (item: SelectableItem) => (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        setSelectedItem(item);
        setDialogOpen(true);
        // open dialog to edit the session title
    };
    const handleClose = () => {
        setDialogOpen(false);
    };
    const updateSelectedSessionTopic = async (item: SelectableItem, topic: string) => {
        const response = await axios.put(
            processApiPath(`/v1/class/sessions`),
            {
                user_uuid: userUuid,
                session: {
                    class_session_topic: topic,
                    class_session_uuid: item.sessionUuid,
                    class_uuid: item.classUuid
                }
            },
            { headers: getUserUuidHeaders(userUuid) }
        );
        item.topic = topic;
        if (response.status === 200) {
            fetchClassInfo();
        }
    };
    const handleEditDialogConfirmed = () => {
        // save the new session title
        const newTopic = (document.getElementById('topic') as HTMLInputElement).value;
        const item = selectedItem;
        if (item) {
            updateSelectedSessionTopic(item, newTopic);
        } else {
            console.log('No item found', selectedSessionUuid);
        }
        setDialogOpen(false);
    };
    return (
        <Loadable isLoading={isLoadingClassData}>
            <Stack>
                <Box sx={{ width: '90%', bgcolor: 'background.paper' }}>
                    <Typography variant="subtitle1" gutterBottom id="select-header" sx={{ paddingLeft: '10px' }}>
                        {t('select-resource')}
                    </Typography>
                    <Dialog open={dialogOpen} onClose={handleClose} aria-labelledby="edit-session-title">
                        <DialogTitle id="edit-session-title">{t('rename-session')}</DialogTitle>
                        <DialogContent>
                            <TextField
                                autoFocus
                                margin="dense"
                                id="topic"
                                label={t('session-name')}
                                type="text"
                                defaultValue={selectedItem?.topic}
                                fullWidth
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleClose}>{t('cancel')}</Button>
                            <Button onClick={handleEditDialogConfirmed}>{t('save')}</Button>
                        </DialogActions>
                    </Dialog>
                    <List dense aria-labeled-by="select-header" sx={{ overflow: 'auto', maxHeight: 200 }}>
                        {selectableItems.map((item, index) => (
                            <ListItem
                                dense
                                key={index}
                                secondaryAction={
                                    <IconButton
                                        edge="end"
                                        aria-label="{t('rename-session')}"
                                        onClick={editSessionTitle(item)}
                                    >
                                        <Edit />
                                    </IconButton>
                                }
                            >
                                <ListItemButton
                                    role={undefined}
                                    selected={item.sessionUuid === selectedItem?.sessionUuid}
                                    onClick={handleListItemClick(item.sessionUuid)}
                                >
                                    <ListItemIcon sx={{ minWidth: '26px' }}>
                                        <MeetingRoom />
                                    </ListItemIcon>
                                    <ListItemText primary={item.title} secondary={item.subTitle} />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                </Box>
                <Box sx={{ height: '80px', paddingTop: '40px' }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={submitDeepLinkPost}
                        style={{ float: 'right', marginRight: '20px' }}
                    >
                        {t('add-item')}
                    </Button>
                </Box>
                <form id="lti-deep-link-form" method="post" action="">
                    <input type="hidden" name="JWT" value="" />
                </form>
            </Stack>
        </Loadable>
    );
};
